import React, { useState, useEffect, useRef, useMemo } from "react";
import { Row, Col, Button } from "antd";
import { withTranslation } from "react-i18next";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import {
  CustomizedAxisTick,
  CustomizedDot,
  CustomizedTooltip,
} from "../CustomChartComponents/CustomChartComponents";
import {
  processChartInfo,
  updateTelemetryInfo,
  getMinDomainValue,
  getMaxDomainValue,
  handleToggle,
  isHumidityDisabled,
  isLimitsDisabled,
  isEventDisabled,
  renderReferenceLines,
  renderHumidityYAxis,
  getChartViewSettings,
  buildAlertSummary,
} from "../../utils/shipmentChartsUtils";
import ReportModal from "../ReportModal/ReportModal";
import { exportTelemetryReport } from "../../utils/exportReport";
import ChartAlertsLegend from "../ChartAlertsLegend/ChartAlertsLegend";

function ShipmentCharts({
  trackNum,
  chartsInfo,
  isStandalone,
  alertsListInfo,
  variant = "full",
  stepsRef,
}) {
  const [chartParameters, setChartParameters] = useState({
    message: "Cargando...",
    status: "loading",
    statusCode: 0,
    track_num: trackNum,
    telemetryInfo: [],
    events: [],
    tempMax: 0,
    tempMin: 0,
    maxRecord: 0,
    minRecord: 0,
    preRegisteredRef: null,
    admittedRef: null,
    deliveredRef: null,
    inReturnRef: null,
    returnedToSenderRef: null,
    periodMinuteGuard: 0,
  });

  const [showChart, setShowChart] = useState({
    showTemperature: true,
    showHumidity: true,
    showLimits: true,
    showEvents: true,
    showAlerts: true,
  });

  const [showReportModal, setShowReportModal] = useState(false);
  const chartRef = useRef(null);

  const alerts = useMemo(() => {
    const listForVariant = alertsListInfo?.[variant]?.alertsList ?? [];

    return buildAlertSummary(listForVariant, chartsInfo, variant);
  }, [alertsListInfo, chartsInfo, variant]);

  useEffect(() => {
    if (chartsInfo.statusCode === 200) {
      const baseTelemetry = chartsInfo?.telemetryInfo?.[variant] ?? [];

      const telemetryDatesForVariant = baseTelemetry.map((d) => d.date);

      const { adjustedEvents, eventsMap, eventTelemetry } = processChartInfo(
        chartsInfo,
        telemetryDatesForVariant,
      );

      const updatedTelemetry = updateTelemetryInfo(
        baseTelemetry,
        adjustedEvents,
        eventTelemetry,
      );

      setChartParameters((prevState) => ({
        ...prevState,
        ...chartsInfo,
        status: "loaded",
        telemetryInfo: updatedTelemetry,
        events: adjustedEvents,
        ...eventsMap,
        maxRecord: Math.max(...baseTelemetry.map((d) => d.temp)),
        minRecord: Math.min(...baseTelemetry.map((d) => d.temp)),
      }));
    } else {
      setChartParameters((prevState) => ({
        ...prevState,
        message: "No se han encontrado datos de telemetría para este envío.",
        status: "loaded",
        statusCode: chartsInfo.statusCode,
      }));
    }
  }, [chartsInfo, variant]);

  const handleBeforeCapture = async (variantForReport) => {
    setShowChart(getChartViewSettings(variantForReport));
  };

  const handleExportReport = ({
    reportType,
    reportData,
    setAllowCancel,
    isStandalone,
    variant,
  }) => {
    return exportTelemetryReport({
      onBeforeCapture: handleBeforeCapture,
      chartRef,
      stepsRef,
      reportType,
      telemetryInfo: chartParameters.telemetryInfo,
      alerts,
      reportData,
      isCancelled: () => false,
      setAllowCancel,
      isStandalone,
      variant,
    });
  };

  return (
    <div className="Charts">
      {chartParameters.status === "loading" ||
      chartParameters.statusCode !== 200 ? (
        <p>{chartParameters.message}</p>
      ) : (
        <Row gutter={16}>
          <Col span={24}>
            <div className="chart-header">
              <h3>Gráficas de telemetría</h3>
              <Button onClick={() => setShowReportModal(true)}>
                Exportar informe
              </Button>
            </div>

            <div className="btn-section">
              <Button
                onClick={() =>
                  handleToggle("showEvents", showChart, setShowChart)
                }
                className={
                  showChart.showEvents ? "events-btn" : "events-btn inactive"
                }
                disabled={isEventDisabled(chartParameters.events)}
              >
                Trazabilidad Logística
              </Button>

              <Button
                onClick={() =>
                  handleToggle("showTemperature", showChart, setShowChart)
                }
                className={
                  showChart.showTemperature ? "temp-btn" : "temp-btn inactive"
                }
              >
                Temperatura
              </Button>

              <Button
                onClick={() =>
                  handleToggle("showHumidity", showChart, setShowChart)
                }
                className={
                  showChart.showHumidity ? "hum-btn" : "hum-btn inactive"
                }
                disabled={
                  isStandalone ||
                  isHumidityDisabled(chartParameters.telemetryInfo)
                }
              >
                Humedad
              </Button>

              <Button
                onClick={() =>
                  handleToggle("showLimits", showChart, setShowChart)
                }
                className={
                  showChart.showLimits ? "limits-btn" : "limits-btn inactive"
                }
                disabled={
                  isStandalone ||
                  isLimitsDisabled(
                    chartParameters.tempMax,
                    chartParameters.tempMin,
                  )
                }
              >
                Rango Temperatura
              </Button>

              <Button
                onClick={() =>
                  handleToggle("showAlerts", showChart, setShowChart)
                }
                className={
                  showChart.showAlerts ? "alerts-btn" : "alerts-btn inactive"
                }
                disabled={isStandalone}
              >
                Alertas
              </Button>
            </div>

            <ChartAlertsLegend />

            <div className="chart" ref={chartRef}>
              <ResponsiveContainer>
                <LineChart
                  data={chartParameters.telemetryInfo}
                  margin={{ top: 40, right: 20, bottom: 80, left: 50 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={<CustomizedAxisTick />} />

                  {/* Eje Y para Temperatura */}
                  <YAxis
                    yAxisId="left"
                    label={{ value: "T. (ºC)", position: "top", offset: 25 }}
                    unit="°C"
                    domain={[
                      getMinDomainValue(chartParameters),
                      getMaxDomainValue(chartParameters),
                    ]}
                  />

                  {/* Eje Y para Humedad */}
                  {renderHumidityYAxis(isStandalone)}

                  <Tooltip content={<CustomizedTooltip />} />

                  {/* Límites de Temperatura */}
                  {showChart.showLimits && (
                    <ReferenceLine
                      y={chartParameters.tempMax}
                      label="Max"
                      stroke="#f8c002"
                      yAxisId="left"
                    />
                  )}
                  {showChart.showLimits && (
                    <ReferenceLine
                      y={chartParameters.tempMin}
                      label="Min"
                      stroke="#f8c002"
                      yAxisId="left"
                    />
                  )}

                  {/* Líneas de referencia de Eventos */}
                  {renderReferenceLines(chartParameters, showChart.showEvents)}

                  {/* Línea de Temperatura */}
                  {showChart.showTemperature && (
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="temp"
                      unit="°C"
                      name="Temperatura"
                      stroke="#ef9928"
                      dot={showChart.showAlerts ? <CustomizedDot /> : false}
                    />
                  )}

                  {/* Línea de Humedad */}
                  {showChart.showHumidity && (
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="humidity"
                      name="Humedad"
                      unit="%"
                      stroke="#3ba3e0"
                      dot={false}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Col>
        </Row>
      )}

      <ReportModal
        visible={showReportModal}
        onClose={() => setShowReportModal(false)}
        telemetryInfo={chartParameters.telemetryInfo}
        alertsList={alerts}
        isStandalone={isStandalone}
        onExportReport={handleExportReport}
        variant={variant}
      />
    </div>
  );
}

export default withTranslation()(ShipmentCharts);
